import { useState, useEffect, useCallback, useRef } from 'react';
import './ResizablePanel.css';

interface ResizablePanelProps {
  leftPanel: React.ReactNode;
  rightPanel: React.ReactNode;
  defaultLeftWidth?: number;
  minLeftWidth?: number;
  maxLeftWidth?: number;
  storageKey?: string;
}

export default function ResizablePanel({
  leftPanel,
  rightPanel,
  defaultLeftWidth = 400,
  minLeftWidth = 300,
  maxLeftWidth = 600,
  storageKey,
}: Readonly<ResizablePanelProps>) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [leftWidth, setLeftWidth] = useState<number>(() => {
    if (storageKey) {
      const saved = localStorage.getItem(storageKey);
      if (saved) {
        const parsed = parseInt(saved, 10);
        if (!isNaN(parsed) && parsed >= minLeftWidth && parsed <= maxLeftWidth) {
          return parsed;
        }
      }
    }
    return defaultLeftWidth;
  });
  const [isDragging, setIsDragging] = useState(false);

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDoubleClick = useCallback(() => {
    setLeftWidth(defaultLeftWidth);
    if (storageKey) {
      localStorage.setItem(storageKey, String(defaultLeftWidth));
    }
  }, [defaultLeftWidth, storageKey]);

  useEffect(() => {
    if (!isDragging) return;

    const handleMouseMove = (e: MouseEvent) => {
      if (!containerRef.current) return;

      const containerRect = containerRef.current.getBoundingClientRect();
      let newWidth = e.clientX - containerRect.left;

      // Clamp to min/max
      newWidth = Math.max(minLeftWidth, Math.min(maxLeftWidth, newWidth));

      // Ensure panel doesn't exceed container width minus handle and minimum right panel space
      const maxAllowedWidth = containerRect.width - 100; // Leave at least 100px for right panel
      newWidth = Math.min(newWidth, maxAllowedWidth);

      setLeftWidth(newWidth);
    };

    const handleMouseUp = () => {
      setIsDragging(false);
      if (storageKey) {
        localStorage.setItem(storageKey, String(leftWidth));
      }
    };

    // Prevent text selection during drag
    document.body.style.userSelect = 'none';
    document.body.style.cursor = 'col-resize';

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.body.style.userSelect = '';
      document.body.style.cursor = '';
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, leftWidth, minLeftWidth, maxLeftWidth, storageKey]);

  // Save width on change when dragging ends
  useEffect(() => {
    if (!isDragging && storageKey) {
      localStorage.setItem(storageKey, String(leftWidth));
    }
  }, [isDragging, leftWidth, storageKey]);

  return (
    <div ref={containerRef} className="resizable-container">
      <div className="resizable-left" style={{ width: leftWidth }}>
        {leftPanel}
      </div>
      <div
        className={`resizable-handle ${isDragging ? 'dragging' : ''}`}
        onMouseDown={handleMouseDown}
        onDoubleClick={handleDoubleClick}
        aria-label="Resize panels"
        role="separator"
        aria-orientation="vertical"
      />
      <div className="resizable-right">
        {rightPanel}
      </div>
    </div>
  );
}
